import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import type { PaidPlan } from '../../utils/accountApi';
import { PLANS, getUserPlan } from '../../utils/plans';
import { BUSINESS } from '../../legal/legalDetails';
import LegalLinks from '../Legal/LegalLinks';
import CheckoutDialog from './CheckoutDialog';

interface PricingPageProps {
  notice?: string;
}

const COMPARISON: Array<{ feature: string; standard: boolean; pro: boolean }> = [
  { feature: 'Live Coding (JS runner + React sandbox)', standard: true, pro: true },
  { feature: 'Technical Q&A', standard: true, pro: true },
  { feature: 'Behavioural interviews', standard: true, pro: true },
  { feature: 'AI voice interviewer', standard: true, pro: true },
  { feature: 'Scored feedback and interview history', standard: true, pro: true },
  { feature: 'System Design', standard: false, pro: true },
  { feature: 'Architecture Review', standard: false, pro: true },
  { feature: 'CV Review', standard: false, pro: true },
  { feature: 'Job-description Focus interviews', standard: false, pro: true },
  { feature: 'Case Study Prep', standard: false, pro: true },
  { feature: 'Multi-stage sessions', standard: false, pro: true },
  { feature: 'Camera & audio recordings', standard: false, pro: true },
  { feature: 'Calendar scheduling and transcript export', standard: false, pro: true },
];

const FAQS = [
  {
    q: 'Can I cancel at any time?',
    a: "Yes. Cancel from your Plan & billing page in a couple of clicks. You won't be charged again and you keep access until the end of the month you've paid for.",
  },
  {
    q: 'What if it isn’t for me?',
    a: 'Cancel within 14 days of subscribing and we’ll refund you, minus a proportionate amount for the days you used. See our Refunds & Cancellation Policy.',
  },
  {
    q: 'Can I switch plans later?',
    a: 'Any time. Upgrades start straight away and you only pay the difference for the rest of the month; downgrades credit the difference to your next payment.',
  },
  {
    q: 'Is my payment secure?',
    a: 'Payments are processed by Stripe, a PCI-DSS Level 1 provider. Your card details never touch our servers.',
  },
  {
    q: 'Can I try it first?',
    a: 'Yes. The free 10-minute live-coding demo needs no signup.',
  },
];

const Check: React.FC<{ on: boolean }> = ({ on }) =>
  on ? (
    <span aria-label="Included" className="font-bold text-emerald-500">
      ✓
    </span>
  ) : (
    <span aria-label="Not included" className="text-slate-300">
      —
    </span>
  );

const PricingPage: React.FC<PricingPageProps> = ({ notice }) => {
  const { user, signOut } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState<PaidPlan | null>(null);
  const currentPlan = user?.accountType === 'account' ? getUserPlan(user) : null;
  const checkoutCancelled = new URLSearchParams(window.location.search).has('checkout');

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 pb-40 text-white">
        <header className="mx-auto flex max-w-6xl items-center justify-between px-5 py-5">
          <a href="/" className="text-lg font-bold tracking-tight text-white no-underline">
            {BUSINESS.productName}
          </a>
          <nav className="flex items-center gap-4 text-sm">
            <a href="/demo" className="text-indigo-200 no-underline hover:text-white">
              Free demo
            </a>
            {user ? (
              <>
                {currentPlan !== 'none' && (
                  <a href="/" className="text-indigo-200 no-underline hover:text-white">
                    Back to app
                  </a>
                )}
                <button
                  type="button"
                  onClick={signOut}
                  className="rounded-lg border border-white/20 px-3 py-1.5 text-white hover:bg-white/10"
                >
                  Sign out
                </button>
              </>
            ) : (
              <a
                href="/"
                className="rounded-lg border border-white/20 px-3 py-1.5 text-white no-underline hover:bg-white/10"
              >
                Sign in
              </a>
            )}
          </nav>
        </header>

        <div className="mx-auto max-w-3xl px-5 pt-10 text-center">
          {notice && (
            <p className="mx-auto mb-6 inline-block rounded-full bg-amber-400/20 px-4 py-1.5 text-sm font-medium text-amber-200">
              {notice}
            </p>
          )}
          <h1 className="m-0 text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
            Walk into your next interview{' '}
            <span className="bg-gradient-to-r from-indigo-300 to-pink-300 bg-clip-text text-transparent">
              already warmed up
            </span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-lg text-indigo-100">
            Practise with an AI interviewer that listens, pushes back with
            follow-ups and scores every answer, as many times as you like.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3 text-xs font-medium text-indigo-100">
            <span className="rounded-full bg-white/10 px-3 py-1">✓ Cancel any time</span>
            <span className="rounded-full bg-white/10 px-3 py-1">✓ 14-day refund window</span>
            <span className="rounded-full bg-white/10 px-3 py-1">🔒 Secure checkout by Stripe</span>
          </div>
        </div>
      </div>

      <main className="mx-auto -mt-28 max-w-5xl px-5">
        {checkoutCancelled && (
          <p role="status" className="mx-auto mb-6 max-w-xl rounded-lg bg-white p-3 text-center text-sm text-slate-600 shadow">
            Checkout was cancelled and you haven&apos;t been charged.
          </p>
        )}
        <div className="grid gap-6 md:grid-cols-2">
          {PLANS.map((plan) => {
            const isPro = plan.id === 'pro';
            const isCurrent = currentPlan === plan.id;
            return (
              <article
                key={plan.id}
                aria-label={`${plan.name} plan`}
                className={`relative flex flex-col rounded-3xl bg-white p-8 shadow-xl ${
                  isPro ? 'ring-2 ring-indigo-500 md:-translate-y-3' : 'ring-1 ring-slate-200'
                }`}
              >
                {isPro && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-gradient-to-r from-indigo-500 to-purple-600 px-4 py-1 text-xs font-bold uppercase tracking-wide text-white shadow">
                    Most popular
                  </span>
                )}
                <h2 className="m-0 text-xl font-bold text-slate-900">{plan.name}</h2>
                <p className="mt-1 min-h-[40px] text-sm text-slate-500">{plan.tagline}</p>
                <p className="mt-4 flex items-baseline gap-1">
                  <span className="text-5xl font-extrabold tracking-tight text-slate-900">
                    £{plan.monthlyPrice}
                  </span>
                  <span className="text-slate-500">/month</span>
                </p>
                <p className="mt-1 text-xs text-slate-400">
                  Billed monthly · cancel any time
                </p>
                <button
                  type="button"
                  onClick={() => setSelectedPlan(plan.id)}
                  disabled={isCurrent}
                  className={`mt-6 w-full rounded-xl px-4 py-3 text-base font-semibold transition disabled:cursor-default disabled:opacity-60 ${
                    isPro
                      ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg hover:from-indigo-600 hover:to-purple-700'
                      : 'bg-slate-900 text-white hover:bg-slate-800'
                  }`}
                >
                  {isCurrent ? 'Your current plan' : isPro ? 'Go Pro' : 'Choose Standard'}
                </button>
                {plan.intro && (
                  <p className="mb-0 mt-6 text-sm font-semibold text-slate-900">{plan.intro}</p>
                )}
                <ul className={`m-0 list-none space-y-2.5 p-0 ${plan.intro ? 'mt-3' : 'mt-6'}`}>
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex gap-2 text-sm text-slate-700">
                      <span className={isPro ? 'text-indigo-500' : 'text-emerald-500'}>✓</span>
                      {feature}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>

        <section className="mt-16">
          <h2 className="text-center text-2xl font-bold text-slate-900">Compare plans</h2>
          <div className="mt-6 overflow-hidden rounded-2xl bg-white shadow ring-1 ring-slate-200">
            <table className="w-full text-left text-sm">
              <thead className="bg-slate-50 text-slate-500">
                <tr>
                  <th className="px-5 py-3 font-medium">Feature</th>
                  <th className="px-5 py-3 text-center font-medium">Standard</th>
                  <th className="px-5 py-3 text-center font-medium text-indigo-600">Pro</th>
                </tr>
              </thead>
              <tbody>
                {COMPARISON.map((row) => (
                  <tr key={row.feature} className="border-t border-slate-100">
                    <td className="px-5 py-3 text-slate-700">{row.feature}</td>
                    <td className="px-5 py-3 text-center">
                      <Check on={row.standard} />
                    </td>
                    <td className="px-5 py-3 text-center">
                      <Check on={row.pro} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        <section className="mx-auto mt-16 max-w-3xl">
          <h2 className="text-center text-2xl font-bold text-slate-900">Questions</h2>
          <div className="mt-6 space-y-3">
            {FAQS.map((item) => (
              <details key={item.q} className="group rounded-xl bg-white p-5 shadow-sm ring-1 ring-slate-200">
                <summary className="cursor-pointer list-none font-semibold text-slate-900">
                  {item.q}
                </summary>
                <p className="mb-0 mt-2 text-sm text-slate-600">{item.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-12 max-w-3xl rounded-2xl bg-white p-6 text-xs leading-relaxed text-slate-500 ring-1 ring-slate-200">
          <p className="m-0">
            <strong className="text-slate-700">Subscription terms.</strong>{' '}
            Plans are billed monthly in advance in GBP and renew automatically
            until cancelled. {BUSINESS.vatNote}. Cancel any time from Plan
            &amp; billing; access continues to the end of the paid month. UK
            consumers can cancel within 14 days of first subscribing for a
            refund, less a proportionate amount for the service used. Full
            details are in our <a href="/terms" className="text-indigo-600 underline">Terms of Service</a>,{' '}
            <a href="/refunds" className="text-indigo-600 underline">Refunds &amp; Cancellation Policy</a>{' '}
            and <a href="/privacy" className="text-indigo-600 underline">Privacy Policy</a>.
          </p>
        </section>
      </main>

      <footer className="mt-16 border-t border-slate-200 py-8 text-center text-slate-500">
        <LegalLinks />
        <p className="mb-0 mt-3 text-xs">
          © {new Date().getFullYear()} {BUSINESS.legalName}
        </p>
      </footer>

      {selectedPlan && (
        <CheckoutDialog plan={selectedPlan} onClose={() => setSelectedPlan(null)} />
      )}
    </div>
  );
};

export default PricingPage;
